import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Address } from '../addresses/Address.js';
import { File } from '../files/File.js';
import { STInvoiceItem } from './STInvoiceItem.js';
export class STInvoiceMeta extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Has this been charged automatically via outstanding amount?
         */
        this.backgroundCharge = false;
        /**
         * VATPercentage should be zero in countries outside Belgium in EU
         */
        this.VATPercentage = 21;
        this.items = [];
        /**
         * Sometimes we need to calculate an invoice in reverse when we have a fixed price including VAT,
         * but need to calculatle the price excluding VAT.
         */
        this.areItemsIncludingVAT = false;
        this.companyEmail = null;
        this.companyVATNumber = null;
        this.companyNumber = null;
        this.ipAddress = null;
        this.userAgent = null;
        this.stripeAccountId = null;
    }
    /**
     * @deprecated
     * Depending on areItemsIncludingVAT, this can either be including or excluding VAT
     */
    get itemPrice() {
        return this.items.reduce((price, item) => price + item.price, 0);
    }
    includingVATToExcludingVAT(price) {
        // Always only round the VAT, not other prices
        return price - this.getVATOnIncludingVATAmount(price);
    }
    excludingVATToIncludingVAT(price) {
        // Always only round the VAT, not other prices
        return price + this.getVATOnExcludingVATAmount(price);
    }
    getVATOnIncludingVATAmount(price) {
        return Math.round(price * this.VATPercentage / (100 + this.VATPercentage));
    }
    getVATOnExcludingVATAmount(price) {
        // Make sure price result doesn't depend on the sign of the price
        return Math.round(Math.abs(price) * this.VATPercentage / 100) * Math.sign(price);
    }
    get useLegacyRounding() {
        // In the past we didn't round the price without VAT if we calculated starting from a price inclusive VAT
        // in that case, we only rounded the VAT
        return this.date && this.date < new Date('2025-12-01');
    }
    get priceWithoutVAT() {
        if (this.useLegacyRounding) {
            const itemPrice = this.itemPrice;
            if (this.areItemsIncludingVAT) {
                return itemPrice - this.VAT;
            }
            return itemPrice;
        }
        if (this.areItemsIncludingVAT) {
            // We round at individual item level
            // because PEPPOL requires prices with max 2 decimals on every line level, meaning we need to round.
            return this.items.reduce((price, item) => price + this.includingVATToExcludingVAT(item.price), 0);
        }
        return this.items.reduce((price, item) => price + item.price, 0);
    }
    get VAT() {
        if (this.useLegacyRounding && this.areItemsIncludingVAT) {
            // Subtract VAT and round
            // Need to be careful with circular calls
            return this.getVATOnIncludingVATAmount(this.itemPrice);
        }
        return this.getVATOnExcludingVATAmount(this.priceWithoutVAT);
    }
    get priceWithVAT() {
        return this.priceWithoutVAT + this.VAT;
    }
    /**
     * How much to add or remove to priceWithVAT to get to the payable amount. We can get a rounding error of 1 cent positive or negative if we calculate from a given price inclusive VAT.
     *
     * 1 cent if we need to add 1 cent
     * -1 cent if we need to remove 1 cent from the priceWithVAT to get to the payable amount
     */
    get payableRoundingAmount() {
        return this.totalPrice - this.priceWithVAT;
    }
    get totalPrice() {
        const itemPrice = this.itemPrice;
        if (this.areItemsIncludingVAT) {
            return itemPrice;
        }
        return itemPrice + this.VAT;
    }
}
__decorate([
    field({ decoder: DateDecoder, optional: true })
], STInvoiceMeta.prototype, "date", void 0);
__decorate([
    field({ decoder: File, optional: true })
], STInvoiceMeta.prototype, "pdf", void 0);
__decorate([
    field({ decoder: File, optional: true })
], STInvoiceMeta.prototype, "xml", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], STInvoiceMeta.prototype, "backgroundCharge", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STInvoiceMeta.prototype, "VATPercentage", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(STInvoiceItem) })
], STInvoiceMeta.prototype, "items", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 186 })
], STInvoiceMeta.prototype, "areItemsIncludingVAT", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceMeta.prototype, "companyName", void 0);
__decorate([
    field({ decoder: StringDecoder })
], STInvoiceMeta.prototype, "companyContact", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true, nullable: true })
], STInvoiceMeta.prototype, "companyEmail", void 0);
__decorate([
    field({ decoder: Address })
], STInvoiceMeta.prototype, "companyAddress", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], STInvoiceMeta.prototype, "companyVATNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], STInvoiceMeta.prototype, "companyNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 133 })
], STInvoiceMeta.prototype, "ipAddress", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 133 })
], STInvoiceMeta.prototype, "userAgent", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 210 })
], STInvoiceMeta.prototype, "stripeAccountId", void 0);
//# sourceMappingURL=STInvoiceMeta.js.map